import React from "react";
import PropTypes from "prop-types";
import { Grid, Box, Link } from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import classNames from "classnames";
import parse from "html-react-parser";

import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

import PlayButton from "../../components/PlayButton";

import { getThumbnailUrl } from "../../utils/common";

const useStyles = makeStyles(theme =>
  createStyles({
    largeWidePoster: {
      width: "100%",
      overflow: "hidden",
      transition: ".5s ease",
      [theme.breakpoints.up("md")]: {
        maxWidth: 620,
        maxHeight: 376,
      },
    },
    smallWidePoster: {
      width: "100%",
      minHeight: 97,
      overflow: "hidden",
      opacity: 1,
      transition: ".5s ease",
      "&:first-child": {
        marginRight: theme.spacing(1),
      },
      maxWidth: 300,
      maxHeight: 180,
      [theme.breakpoints.between("sm", "md")]: {
        maxWidth: 400,
        maxHeight: 180 * (400 / 300),
      },
    },
    wideMask: {
      opacity: 0,
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.65)",
      color: "white",
      padding: "5%",
      paddingBottom: 0,
      marginBottom: 10,
      transitionDuration: "0.5s",
      "&:hover": {
        opacity: 1,
      },
      [theme.breakpoints.down("xs")]: {
        display: "none",
      },
    },
    wideMaskSmall: {
      padding: "5%",
    },
    wideMaskTitle: {
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    wideMaskLargeTitle: {
      ...theme.typography.h2,
      fontWeight: 500,
    },
    wideMaskSmallTitle: {
      ...theme.typography.caption,
      fontSize: 24,
      fontWeight: 700,
    },
    metaContainer: {
      marginTop: 12,
    },
    metaLargeText: {
      ...theme.typography.tags,
      color: theme.palette.common.grey[300],
      marginBottom: theme.spacing(0.75),
    },
    metaText: {
      ...theme.typography.caption,
      color: theme.palette.common.grey[300],
      marginBottom: theme.spacing(0.75),
      fontSize: 14,
    },
    blockWithText: {
      width: "28rem",
      maxWidth: "34rem",
      overflow: "hidden",
      position: "relative",
      lineHeight: "1.2em",
      maxHeight: "3.6em",
      textAlign: "justify",
      marginRight: "-1em",
      paddingRight: "1em",
      marginBottom: "0.5em",
      "&&:before": {
        content: "'......'",
        position: "absolute",
        right: 0,
        bottom: 0,
      },
      "&&:after": {
        content: "''",
        position: "absolute",
        right: 0,
        width: "1em",
        height: "1em",
        marginTop: "0.2em",
        background: "transparent",
      },
    },
    blockWithText2: {
      maxWidth: "34rem",
      overflow: "hidden",
      position: "relative",
      lineHeight: "1.2em",
      maxHeight: "3.6em",
      textAlign: "justify",
      marginRight: "-1em",
      paddingRight: "1em",
      marginBottom: "0.5em",
      [theme.breakpoints.down("sm")]: {
        marginTop: "1em",
        display: "none",
      },
    },
    playButtonContainerLg: {
      marginTop: theme.spacing(3),
      [theme.breakpoints.only("md")]: {
        marginTop: theme.spacing(2),
      },
      [theme.breakpoints.down("sm")]: {
        marginTop: theme.spacing(10),
      },
      [theme.breakpoints.only("xs")]: {
        marginTop: theme.spacing(0.75),
      },
    },
    playButtonContainerSm: {
      marginTop: theme.spacing(4),
      [theme.breakpoints.only("lg")]: {
        marginTop: theme.spacing(2.5),
      },
      [theme.breakpoints.only("md")]: {
        marginTop: theme.spacing(0.5),
      },
      [theme.breakpoints.only("xs")]: {
        marginTop: theme.spacing(0.15),
      },
    },
  }),
);

const WideVideoAlbumItem = ({
  size = "sm",
  id,
  title,
  description,
  others,
  showDesceiption,
  poster,
}) => {
  const classes = useStyles();

  const WideHoverMask = () => {
    const wideMaskSizeTitleClass =
      size === "lg" ? classes.wideMaskLargeTitle : classes.wideMaskSmallTitle;
    const metaTextClass =
      size === "lg" ? classes.metaLargeText : classes.metaText;

    return (
      <>
        <div
          className={classNames(classes.wideMask, {
            [classes.wideMaskSmall]: size !== "lg",
          })}
        >
          <Grid container>
            <Grid item xs={12}>
              <div
                className={classNames(
                  classes.wideMaskTitle,
                  wideMaskSizeTitleClass,
                )}
              >
                {title}
              </div>
            </Grid>
            <Grid item xs={12}>
              <div
                className={classNames(metaTextClass, {
                  [classes.metaContainer]: size === "lg",
                })}
              >
                {others}
              </div>
            </Grid>
            <Grid item xs={12}>
              <Box
                className={classes.blockWithText2}
                display={size === "lg" || showDesceiption ? "block" : "none"}
              >
                {parse(description.replace(/^<p>/, "").replace(/<\/p>$/, ""))}
              </Box>
            </Grid>
            <Grid container item justify='center'>
              <Grid item>
                <Box
                  className={classNames(
                    { [classes.playButtonContainerLg]: size === "lg" },
                    { [classes.playButtonContainerSm]: size === "sm" },
                  )}
                >
                  <PlayButton size={size} vid={id} />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </>
    );
  };

  const largeWideVideo = () => {
    return (
      <>
        <Box position='relative'>
          {/* layout error */}
          {/* <LazyLoadImage
            className={classes.largeWidePoster}
            alt={title}
            src={`${staticUrl}${poster}`}
            effect='blur'
          /> */}
          <div className={classes.largeWidePoster}>
            <img
              src={getThumbnailUrl({ url: poster, ext: "jpg" })}
              alt={title}
              className={classes.largeWidePoster}
            />
            <WideHoverMask
              size='lg'
              video_id={id}
              title={title}
              description={description}
            />
          </div>
        </Box>
      </>
    );
  };

  const smallWideVideo = () => {
    return (
      <>
        <Box position='relative'>
          <LazyLoadImage
            className={classes.smallWidePoster}
            alt={title}
            src={getThumbnailUrl({ url: poster, ext: "jpg" })}
            effect='blur'
          />
          <WideHoverMask
            video_id={id}
            title={title}
            description={description}
          />
        </Box>
      </>
    );
  };

  const render = () => {
    if (size === "sm") {
      return smallWideVideo();
    } else {
      return largeWideVideo();
    }
  };

  return (
    <>
      <Link href={`/video/detail/trailer/${id}`} underline='none'>
        {render()}
      </Link>
    </>
  );
};

WideVideoAlbumItem.defaultProps = {};

WideVideoAlbumItem.propsTypes = {};

export default WideVideoAlbumItem;
